import { html, nothing, type TemplateResult } from "lit-html";

import { api, ApiError } from "../api.js";
import {
  computeMasterPasswordHash,
  deriveKekForLogin,
  persistUnlocked,
  unlockLocal,
} from "../client-crypto.js";
import { getDeviceFingerprint } from "../device-fingerprint.js";
import { setRoute, setUser, showToast, setLoading, rerender, state as appState } from "../main.js";
import { toBase64 } from "@shared/crypto/bytes.js";

type DeviceStatus = "approved" | "pending_email" | "pending_throttled" | "pending_legacy";

let formState = {
  email: "", password: "",
  totp: "", recovery: "", useRecovery: false,
  needsTotp: false, busy: false, err: "",
  // Device flow overlay state (post-login)
  pendingApprovalEmail: null as string | null,
  pendingThrottled: false,
};

async function submit(): Promise<void> {
  if (formState.busy) return;
  formState.busy = true;
  formState.err = "";
  setLoading(true);
  try {
    const kdf = await api.kdfParams(formState.email);
    const kek = await deriveKekForLogin({
      password: formState.password,
      kdfSalt: Uint8Array.from(atob(kdf.kdf_salt_b64), (c) => c.charCodeAt(0)),
      kdfParams: kdf.kdf_params as { algo: "argon2id"; m: number; t: number; p: number; hashLength: number },
    });
    const mph = computeMasterPasswordHash({
      kek,
      kdfSalt: Uint8Array.from(atob(kdf.kdf_salt_b64), (c) => c.charCodeAt(0)),
    });

    // Compute (or read cached) device fingerprint for Microsoft Entra-style
    // device recognition. Hash is stable across sessions on the same
    // physical browser+device.
    const fingerprint = await getDeviceFingerprint().catch(() => null);

    const loginBody: Record<string, unknown> = {
      email: formState.email.toLowerCase(),
      master_password_hash_b64: toBase64(mph),
      turnstile_token: "DEV_NO_TURNSTILE",
    };
    if (fingerprint) loginBody.device_fingerprint_b64 = fingerprint;
    if (formState.needsTotp) {
      if (formState.useRecovery && formState.recovery) loginBody.recovery_code = formState.recovery;
      else if (formState.totp) loginBody.totp_code = formState.totp;
    }

    const resp = await api.login(loginBody) as {
      user: { id: string; email: string; name: string; role: string; must_change_pw: boolean };
      wrapped: Record<string, string | null>;
      device_status: DeviceStatus;
      device_bound_id: string | null;
      approval_email_to: string | null;
    };

    // Unlock locally regardless of device status (master pw was valid).
    const unlocked = await unlockLocal({
      kek,
      wrapped_ask_b64: resp.wrapped.wrapped_ask_b64 as string,
      wrapped_priv_b64: resp.wrapped.wrapped_priv_b64 as string,
      wrapped_metadata_key_b64: resp.wrapped.wrapped_metadata_key_b64 as string,
      ecdh_pub_jwk: resp.wrapped.ecdh_pub_jwk as string,
      signing_pub_jwk: (resp.wrapped.signing_pub_jwk as string | null) ?? null,
    });
    (window as unknown as { __unlocked?: unknown }).__unlocked = unlocked;

    if (resp.device_status === "pending_email" || resp.device_status === "pending_throttled") {
      // First-ever login from this browser. The server registered the
      // device as pending. Either an approval email was sent ("pending_email"),
      // or the per-user throttle was hit and the user must wait / ask
      // admin to approve manually ("pending_throttled").
      // We do NOT enter the vault until the device is approved.
      formState.pendingApprovalEmail = resp.approval_email_to;
      formState.pendingThrottled = resp.device_status === "pending_throttled";
      rerender();
      return;
    }

    persistUnlocked(unlocked);
    setUser({
      id: resp.user.id, email: resp.user.email, name: resp.user.name, role: resp.user.role,
      must_change_pw: Boolean(resp.user.must_change_pw),
    });
    if (resp.user.must_change_pw) {
      showToast(
        appState.lang === "en"
          ? "Temporary password detected. Please set a new master password before continuing."
          : "仮パスワードを検出しました。続行する前に新しいマスターパスワードを設定してください。",
      );
      setRoute("settings");
    } else {
      showToast(appState.lang === "en" ? "Welcome back" : "おかえりなさい");
      setRoute("vault");
    }
  } catch (err) {
    if (err instanceof ApiError && err.code === "mfa_required") {
      formState.needsTotp = true;
      formState.err = appState.lang === "en" ? "2FA code required" : "2FA コードが必要です";
    } else {
      formState.err = (err as Error).message;
    }
  } finally {
    formState.busy = false;
    setLoading(false);
  }
}

function renderPendingApprovalOverlay(): TemplateResult {
  if (formState.pendingApprovalEmail === null && !formState.pendingThrottled) return html``;
  if (formState.pendingThrottled) {
    return html`
      <div class="device-flow-overlay">
        <div class="device-flow-card">
          <div class="big-icon" style="color: var(--status-pending);">!</div>
          <h2>
            <span data-lang="en">Too many new devices</span><span data-lang="jp">新しいデバイスが多すぎます</span>
          </h2>
          <p>
            <span data-lang="en">You have requested too many device authorizations in the last hour. For security, we have paused new approval emails. Please wait, or ask your administrator to approve this device manually from the admin Devices panel.</span><span data-lang="jp">過去 1 時間に新しいデバイスの認証リクエストが多すぎました。セキュリティ上、承認メールの送信を一時停止しています。しばらく待つか、管理者に管理画面のデバイス一覧から手動で承認してもらってください。</span>
          </p>
          <button class="primary" style="width: 100%; margin-top: 16px;"
            @click=${() => { formState.pendingApprovalEmail = null; formState.pendingThrottled = false; rerender(); }}>
            <span data-lang="en">Understood</span><span data-lang="jp">了解</span>
          </button>
        </div>
      </div>
    `;
  }
  return html`
    <div class="device-flow-overlay">
      <div class="device-flow-card">
        <div class="big-icon">✉</div>
        <h2>
          <span data-lang="en">Authorize this device</span><span data-lang="jp">このデバイスを承認</span>
        </h2>
        <p>
          <span data-lang="en">For security, new devices need a one-time authorization. We just sent an authorization link to <b>${formState.pendingApprovalEmail ?? "your registered address"}</b>.</span><span data-lang="jp">セキュリティ上、新しいデバイスは一度だけ認証が必要です。承認リンクを <b>${formState.pendingApprovalEmail ?? "登録済みアドレス"}</b> に送信しました。</span>
        </p>
        <p style="font-size: 13px; color: var(--text-2);">
          <span data-lang="en">Click the link in the email, then return here and sign in again. After authorization, this device is recognized automatically.</span><span data-lang="jp">メール内のリンクをクリックしてから、このページに戻って再度サインインしてください。承認後はこのデバイスが自動認識されます。</span>
        </p>
        <button class="primary" style="width: 100%; margin-top: 16px;"
          @click=${() => { formState.pendingApprovalEmail = null; window.location.reload(); }}>
          <span data-lang="en">I clicked the link, sign in again</span><span data-lang="jp">リンクをクリックしました、再サインイン</span>
        </button>
      </div>
    </div>
  `;
}

export function renderLogin(): TemplateResult {
  return html`
    ${renderPendingApprovalOverlay()}
    <div class="login-shell">
      <div class="login-card">
        <h1 style="text-align: center;">
          <span class="brand-wordmark" style="font-size: 28px;">
            <span class="word-omakase">O<span class="first-a">M</span>AKASE</span>
            <span class="word-vault">Vault</span>
          </span>
        </h1>
        <div class="form-row">
          <label>
            <span data-lang="en">Email</span><span data-lang="jp">メールアドレス</span>
          </label>
          <input type="email" autocomplete="username" .value=${formState.email}
            @input=${(e: InputEvent) => (formState.email = (e.target as HTMLInputElement).value)} />
        </div>
        <div class="form-row">
          <label>
            <span data-lang="en">Master password</span><span data-lang="jp">マスターパスワード</span>
          </label>
          <input type="password" autocomplete="current-password" .value=${formState.password}
            @input=${(e: InputEvent) => (formState.password = (e.target as HTMLInputElement).value)}
            @keydown=${(e: KeyboardEvent) => e.key === "Enter" && void submit()} />
        </div>
        ${formState.needsTotp
          ? formState.useRecovery
            ? html`<div class="form-row">
                <label>
                  <span data-lang="en">Recovery code (XXXX-XXXX)</span><span data-lang="jp">リカバリコード (XXXX-XXXX)</span>
                </label>
                <input type="text" autocomplete="off" maxlength="20"
                  .value=${formState.recovery}
                  @input=${(e: InputEvent) => {
                    formState.recovery = (e.target as HTMLInputElement).value.toUpperCase();
                  }}
                  @keydown=${(e: KeyboardEvent) => e.key === "Enter" && void submit()} />
                <p style="font-size: 11px; color: var(--text-2); margin: 4px 0 0;">
                  <a href="#" @click=${(e: Event) => { e.preventDefault(); formState.useRecovery = false; formState.err = ""; rerender(); }}>
                    <span data-lang="en">Use authenticator code instead</span><span data-lang="jp">認証アプリのコードを使う</span>
                  </a>
                </p>
              </div>`
            : html`<div class="form-row">
                <label>
                  <span data-lang="en">2FA code (Google Authenticator)</span><span data-lang="jp">2FA コード (Google Authenticator)</span>
                </label>
                <input type="text" inputmode="numeric" pattern="[0-9]{6}" maxlength="6"
                  autocomplete="one-time-code"
                  .value=${formState.totp}
                  @input=${(e: InputEvent) => {
                    formState.totp = (e.target as HTMLInputElement).value.replace(/\D/g, "").slice(0, 6);
                  }}
                  @keydown=${(e: KeyboardEvent) => e.key === "Enter" && void submit()} />
                <p style="font-size: 11px; color: var(--text-2); margin: 4px 0 0;">
                  <a href="#" @click=${(e: Event) => { e.preventDefault(); formState.useRecovery = true; formState.err = ""; rerender(); }}>
                    <span data-lang="en">Lost your authenticator? Use a recovery code</span><span data-lang="jp">認証アプリを紛失？リカバリコードを使う</span>
                  </a>
                </p>
              </div>`
          : nothing}
        ${formState.err ? html`<p style="color: var(--status-error); font-size: 13px;">${formState.err}</p>` : nothing}
        <button class="primary" style="width: 100%; margin-top: 8px;" ?disabled=${formState.busy}
          @click=${() => void submit()}>
          ${formState.busy
            ? html`<span data-lang="en">Signing in…</span><span data-lang="jp">サインイン中…</span>`
            : html`<span data-lang="en">Sign in</span><span data-lang="jp">サインイン</span>`}
        </button>
        ${(() => {
          const st = (window as unknown as { __app_state?: { genesisCompleted?: boolean } }).__app_state;
          if (st && st.genesisCompleted === true) return nothing;
          return html`<p style="text-align: center; font-size: 12px; color: var(--text-2); margin-top: 16px;">
            <a href="#" @click=${(e: Event) => { e.preventDefault(); setRoute("genesis"); }}>
              <span data-lang="en">First setup (Super User)</span><span data-lang="jp">初期設定 (Super User)</span>
            </a>
          </p>`;
        })()}
      </div>
    </div>
  `;
}
